import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'

// Load components
import { Grid, Image, Box, Button, SkeletonCircle, SkeletonText, Skeleton } from '@chakra-ui/react'
import Text from '../UI//Text/Text'
import LotteryBall from '../Lottery/LotteryBall'

// Load helpers
import { toSlug } from '../../helper/string'
import { getCdnUrl } from 'helper/cdn'
import { formatDateTime, getLanguageISO } from 'helper/date'
//import { formatPrice, convertFromPence } from 'helper/currency'
import { sortOrderBalls } from 'helper/lotteries'

// Load hooks
import { useGamesQuery } from 'hooks/queries/useGames'

const LOADING_ARRAY = [1, 2, 3, 4]

const loadStyle = {
    fadeDuration: 0.8,
    speed: 1
}

const timeOptions = {
    hour: 'numeric',
    minute: '2-digit'
}

const dateOptions = {
    month: 'short',
    weekday: 'short',
    day: '2-digit'
}

const ResultRow = ({
    hasNumbers,
    mainNumbers,
    bonusNumbers,
    drawTime,
    logo,
    logoAlt,
    //jackpot,
    hasLeadZero,
    resultUrl,
    playUrl,
    isLoading,
    navigate,
    lotteryId
}) => {
    const language = useSelector((state) => state.user.language)
    const date = formatDateTime({
        dateSrc: drawTime,
        dateOptions,
        timeOptions,
        timeZone: getLanguageISO(language)
    })

    return (
        <Box>
            <Box
                display="flex"
                flexDirection={{ base: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems="center"
                mb={1}
            >
                <SkeletonCircle
                    width="auto"
                    height="auto"
                    {...loadStyle}
                    mb={{ base: 4, md: 0 }}
                    isLoaded={!isLoading}
                >
                    {typeof logo === 'string' && (
                        <Image
                            maxH="50px"
                            width="auto"
                            objectFit="cover"
                            ignoreFallback={true}
                            alt={`${logoAlt}-logo`}
                            src={getCdnUrl(logo)}
                        />
                    )}
                </SkeletonCircle>
                <SkeletonText
                    {...loadStyle}
                    my={{ base: 2, md: 0 }}
                    textAlign={{ base: 'center', md: 'right' }}
                    noOfLines={1}
                    isLoaded={!isLoading}
                >
                    <Text
                        textStyle="semiBoldFontFamily"
                        textAlign={{ base: 'left', md: 'right' }}
                        fontSize="xs"
                    >
                        {date.time} {date.date}
                    </Text>
                    {/* <Text
                        textStyle="semiBoldFontFamily"
                        textAlign={{ base: 'left', md: 'right' }}
                        fontSize="sm"
                    >
                        {formatPrice(convertFromPence(jackpot))}
                    </Text> */}
                </SkeletonText>
            </Box>
            <Skeleton {...loadStyle} isLoaded={!isLoading}>
                <Box
                    display="flex"
                    flexDirection={{ base: 'column', md: 'row' }}
                    alignItems="center"
                >
                    {hasNumbers && (
                        <Grid display="flex" width="100%" flexWrap="wrap" px={8}>
                            <>
                                {sortOrderBalls({ lotteryId, balls: mainNumbers }).map((ball) => (
                                    <LotteryBall
                                        key={`board_${ball}`}
                                        number={ball}
                                        ballType="selected"
                                        className="notranslate"
                                        padLeading={hasLeadZero}
                                        m="2px"
                                        size="lg"
                                    />
                                ))}
                                {bonusNumbers &&
                                    bonusNumbers.map((ball, index) => (
                                        <LotteryBall
                                            key={`board_${ball}`}
                                            number={ball}
                                            ballType={`bonus${index + 1}`}
                                            className="notranslate"
                                            padLeading={hasLeadZero}
                                            m="2px"
                                            size="lg"
                                        />
                                    ))}
                            </>
                        </Grid>
                    )}
                    {!hasNumbers && (
                        <Text
                            scope="global"
                            tag="resultStatusDescription.pendingResult"
                            text="The results have not been drawn yet. Please check back after the draw."
                        />
                    )}

                    <Box
                        display="flex"
                        flexDirection={{ base: 'row', md: 'column' }}
                        justifyContent="space-between"
                        width={{ base: '100%', md: 'auto' }}
                        mt={{ base: 2, md: 0 }}
                        alignSelf="flex-start"
                    >
                        <Button
                            colorScheme="buttonSecondary"
                            size="sm"
                            width={{ base: '48%', md: 'auto' }}
                            mb={{ base: 0, md: 2 }}
                            onClick={() => navigate(playUrl)}
                        >
                            <Text
                                tag="button.playNow"
                                fontSize="inherit"
                                color="inherit"
                                text="Play Now"
                            />
                        </Button>
                        <Button
                            size="sm"
                            width={{ base: '48%', md: 'auto' }}
                            onClick={() => navigate(resultUrl)}
                        >
                            <Text
                                tag="button.moreResults"
                                fontSize="inherit"
                                color="inherit"
                                text="More Results"
                            />
                        </Button>
                    </Box>
                </Box>
            </Skeleton>
        </Box>
    )
}

const RecentDrawsList = () => {
    const { data, isLoading } = useGamesQuery()
    const navigate = useNavigate()
    let draws = data?.previous ?? []

    if (isLoading) {
        return (
            <Box>
                {LOADING_ARRAY.map((item) => (
                    <Box _notLast={{ borderBottom: '1px solid', borderColor: 'white' }} py={4}>
                        <ResultRow isLoading />
                    </Box>
                ))}
            </Box>
        )
    }

    return (
        <Box>
            {draws.map((draw) => {
                const hasNumbers = Object.keys(draw.numbers).length > 0
                const drawTime = draw.gmtDrawTime
                const title = draw.name
                const resultUrl = `/results/${draw.lotteryId}/${toSlug(draw.name)}/${draw.id}`

                return (
                    <Box
                        width="100%"
                        _notLast={{ borderBottom: '1px solid', borderColor: 'white' }}
                        py={4}
                    >
                        <ResultRow
                            hasNumbers={hasNumbers}
                            drawTime={drawTime}
                            logo={draw.logo}
                            mainNumbers={draw?.numbers?.main}
                            bonusNumbers={draw?.extraNumbers?.bonus}
                            logoAlt={title}
                            hasLeadZero={draw.hasLeadZero}
                            resultUrl={resultUrl}
                            playUrl={`play/${draw.url}`}
                            navigate={navigate}
                            lotteryId={draw.lotteryId}
                        />
                    </Box>
                )
            })}
            <Button
                display={{ base: 'none', lg: 'inline-block' }}
                mt={2}
                width="100%"
                onClick={() => navigate('play')}
            >
                <Text tag="button.playNow" color="inherit" fontWeight="semibold" text="Play Now" />
            </Button>
        </Box>
    )
}

export default RecentDrawsList
